import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class MicrosoftErrorService {

    private readonly errorMap: { codes: string[], message: string, title: string }[] = [

        {
            codes: ['AADSTS7000222', 'AADSTS7000215'],
            title: 'Sign-in unavailable',
            message: 'Microsoft sign-in is temporarily unavailable. Please notify your teacher or school administrator to resolve this issue.'
        },
        {
            codes: ['AADSTS50011'],
            title: 'Sign-in unavailable',
            message: 'Microsoft sign-in is not properly configured. Please notify your teacher or school administrator.'
        },
        {
            codes: ['AADSTS700016', 'AADSTS650052'],
            title: 'Access not configured',
            message: 'Your school has not granted access to this application. Please contact your teacher or school administrator.'
        },
        {
            codes: ['AADSTS90002', 'AADSTS900023'],
            title: 'Organization not recognized',
            message: 'Your school\'s Microsoft account could not be verified. Please contact your teacher or school administrator.'
        },
        {
            codes: ['AADSTS53003', 'AADSTS53000', 'AADSTS53001'],
            title: 'Access blocked',
            message: 'Your school\'s security settings are preventing sign-in. Please contact your teacher or school administrator.'
        },

        // El estudiante SÍ puede resolver esto
        {
            codes: ['AADSTS65004', 'AADSTS650053'],
            title: 'Access denied',
            message: 'You declined the permission request. Please try signing in again and accept the permissions to continue.'
        },
        {
            codes: ['AADSTS50020'],
            title: 'Wrong account',
            message: 'This Microsoft account is not associated with your school. Please sign in with your school-provided account.'
        },
        {
            codes: ['AADSTS50057'],
            title: 'Account disabled',
            message: 'Your Microsoft account has been disabled. Please contact your teacher or school administrator.'
        },
        {
            codes: ['AADSTS50055'],
            title: 'Password expired',
            message: 'Your Microsoft password has expired. Please reset your password and try again.'
        },
        {
            codes: ['AADSTS50076', 'AADSTS50079', 'AADSTS50074'],
            title: 'Verification required',
            message: 'Your school requires additional verification to sign in. Please complete the Microsoft verification steps and try again.'
        },
        {
            codes: ['AADSTS70011', 'AADSTS70008', 'AADSTS700082'],
            title: 'Session expired',
            message: 'Your sign-in session has expired. Please try signing in again.'
        },
        {
            codes: ['AADSTS50034'],
            title: 'Account not found',
            message: 'Your Microsoft account was not found. Please verify you are using your school-provided email address.'
        },
        {
            codes: ['AADSTS16000', 'AADSTS16001'],
            title: 'Sign-in required',
            message: 'Please sign in again to continue.'
        },
    ];

    /**
     * Determines if the error message is a Microsoft OAuth/AADSTS error.
     */
    isMicrosoftError(message: string): boolean {
        if (!message) return false;
        return message.includes('OAuth2') ||
            message.includes('AADSTS') ||
            message.includes('error_description=');
    }

    /**
     * Returns a sanitized, user-friendly message and title from a raw Microsoft error.
     * Returns null if it is not a Microsoft error.
     */
    sanitize(rawMessage: string): { title: string; message: string } | null {
        if (!this.isMicrosoftError(rawMessage)) return null;

        const descMatch = rawMessage.match(/error_description=([^&}]+)/);
        if (descMatch) {
            const decoded = decodeURIComponent(descMatch[1].replace(/\+/g, ' '));
            const mapped = this.mapByCode(decoded);
            if (mapped) return mapped;

            // Fallback: strip AADSTS prefix and take the first sentence
            const cleaned = decoded.replace(/AADSTS\d+:\s*/, '').split('.')[0].trim();
            return {
                title: 'Authentication error',
                message: cleaned + '.'
            };
        }

        return null;
    }

    private mapByCode(decoded: string): { title: string; message: string } | null {
        for (const entry of this.errorMap) {
            if (entry.codes.some(code => decoded.includes(code))) {
                return { title: entry.title, message: entry.message };
            }
        }
        return null;
    }
}